import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { RootState } from '../../slices/store';
import type { ChatMessageProps } from './types';
import Visualization from './Visualization';
import profilePlaceholder from '../../assets/images/users/profile_placeholder.jpg';
import './ChatMessage.css';

/**
 * ChatMessage Component
 * Displays a single question or answer message with optional visualization
 *
 * Optimizations:
 * - Wrapped with React.memo to prevent unnecessary re-renders
 * - useMemo for expensive computations
 */
const ChatMessageComponent = ({ message, type }: ChatMessageProps) => {
  const isQuestion = type === 'question';

  // Get user info from Redux state
  const user = useSelector((state: RootState) => state.login.user);
  const userProfilePic = user?.user?.profile_picture_url || profilePlaceholder;

  // Memoize expensive text processing
  const { originalMessageText, messageText, isRestrictionMessage } = useMemo(() => {
    const originalText = message.message_text || message.question || message.answer || '';
    // For answer messages, use data_summary if available, otherwise fall back to original message
    const displayText = !isQuestion && message.data_summary ? message.data_summary : originalText;
    const isRestriction = !isQuestion && (
      originalText.toLowerCase().includes('i apologize') ||
      originalText.toLowerCase().includes('cannot provide') ||
      originalText.toLowerCase().includes('restricted') ||
      originalText.toLowerCase().includes('not allowed') ||
      originalText.toLowerCase().includes('access denied') ||
      originalText.toLowerCase().includes('permission denied')
    );
    return {
      originalMessageText: originalText,
      messageText: displayText,
      isRestrictionMessage: isRestriction
    };
  }, [message.message_text, message.question, message.answer, message.data_summary, isQuestion]);

  return (
    <div className={`chat-message ${isQuestion ? 'question' : 'answer'} ${isRestrictionMessage ? 'restriction' : ''}`}>
      <div className="message-header">
        <div className="message-icon">
          {isQuestion ? (
            <img
              src={userProfilePic}
              alt="You"
              className="profile-image"
              onError={(e) => {
                (e.target as HTMLImageElement).src = profilePlaceholder;
              }}
            />
          ) : (
            <img
              src={profilePlaceholder}
              alt="AI Assistant"
              className="profile-image"
            />
          )}
        </div>
        <div className="message-label">
          {isQuestion ? 'You' : 'AI Assistant'}
        </div>
      </div>

      <div className="message-content">
        {/* Restriction Notice (for restriction messages only) */}
        {isRestrictionMessage && (
          <div className="restriction-notice">
            <div className="restriction-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="restriction-label">Access Restriction</div>
          </div>
        )}

        {/* Summary/Key Insights (for answers only, not for restriction messages) - at top */}
        {!isQuestion && !isRestrictionMessage && message.key_insights && (
          <div className="message-insights">
            <div className="insights-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                <line x1="12" y1="20" x2="12" y2="10" strokeWidth="2" strokeLinecap="round"/>
                <line x1="18" y1="20" x2="18" y2="4" strokeWidth="2" strokeLinecap="round"/>
                <line x1="6" y1="20" x2="6" y2="16" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Key Insights
            </div>
            <div className="insights-content">
              {message.key_insights.split('\n').map((line, idx) => (
                line.trim() && <div key={idx} className="insight-line">{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* Message Text - render HTML content for links */}
        <div
          className="message-text"
          dangerouslySetInnerHTML={{ __html: messageText }}
        />

        {/* Visualization (for answers only, not for restriction messages) */}
        {!isQuestion && !isRestrictionMessage && message.visualization_data && (
          <div className="message-visualization">
            <Visualization data={message.visualization_data} />
          </div>
        )}

        {/* Timestamp */}
        {message.created_at && (
          <div className="message-timestamp">
            {new Date(message.created_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

// PropTypes validation
ChatMessageComponent.propTypes = {
  message: PropTypes.shape({
    message_text: PropTypes.string,
    question: PropTypes.string,
    answer: PropTypes.string,
    message_type: PropTypes.oneOf(['question', 'answer']).isRequired,
    key_insights: PropTypes.string,
    data_summary: PropTypes.string,
    visualization_data: PropTypes.object,
    created_at: PropTypes.string,
  }).isRequired,
  type: PropTypes.oneOf(['question', 'answer']).isRequired,
};

// Wrap with memo for performance optimization
const ChatMessage = memo(ChatMessageComponent);

// Display name for debugging
ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
